import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { apiUrl } from "../../../utils/config";
import { useAppContext } from "../../../context";
import { useSearchParams } from "react-router-dom";
import {
  Calendar, Clock, Users, Check, ChevronRight, ArrowLeft,
  Phone, Mail, RefreshCw, X, AlertCircle, UtensilsCrossed,
  Sparkles, Star, MapPin,
} from "lucide-react";

const API        = apiUrl;
const LICENSE_ID = 2;
const TODAY      = new Date().toISOString().split("T")[0];

// ── Helpers ──────────────────────────────────────────────────────────────────
const nextDays = (n) => {
  const out = [];
  for (let i = 0; i < n; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    out.push({
      iso:   d.toISOString().split("T")[0],
      day:   d.toLocaleDateString("en-US", { weekday: "short" }),
      date:  d.getDate(),
      month: d.toLocaleDateString("en-US", { month: "short" }),
      full:  d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }),
    });
  }
  return out;
};
const DAYS = nextDays(90);

const LUNCH_SLOTS  = ["11:00","11:30","12:00","12:30","13:00","13:30","14:00","14:30"];
const DINNER_SLOTS = ["17:00","17:30","18:00","18:30","19:00","19:30","20:00","20:30","21:00","21:30"];

// ── SVG Floor Map ─────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  available: { table: "#22c55e", chair: "#16a34a", text: "#fff", glow: "#86efac" },
  reserved:  { table: "#f59e0b", chair: "#d97706", text: "#fff", glow: "#fcd34d" },
  occupied:  { table: "#ef4444", chair: "#dc2626", text: "#fff", glow: "#fca5a5" },
  inactive:  { table: "#cbd5e1", chair: "#94a3b8", text: "#64748b", glow: "#e2e8f0" },
};

// Draw a circle table (SVG group) with chairs around it
const CircleTable = ({ table, cx, cy, r, colors, selected, isSelectable, onSelect, onHover }) => {
  const chairR   = 9;
  const armDist  = r + chairR + 6;
  const chairs   = [
    [cx,             cy - armDist],
    [cx + armDist,   cy],
    [cx,             cy + armDist],
    [cx - armDist,   cy],
  ];
  return (
    <g
      style={{ cursor: isSelectable ? "pointer" : "not-allowed" }}
      onClick={() => isSelectable && onSelect(table)}
      onMouseEnter={() => onHover(table)}
      onMouseLeave={() => onHover(null)}
    >
      {/* Glow ring when selected */}
      {selected && (
        <circle cx={cx} cy={cy} r={r + 12} fill="none" stroke={colors.glow} strokeWidth={6} opacity={0.7} />
      )}
      {/* Chairs */}
      {chairs.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={chairR} fill={colors.chair} rx={3} />
      ))}
      {/* Table body */}
      <circle cx={cx} cy={cy} r={r} fill={colors.table} />
      {selected && (
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#fff" strokeWidth={2.5} />
      )}
      {/* Label */}
      <text x={cx} y={cy - 4} textAnchor="middle" fill={colors.text} fontSize={10} fontWeight="bold" fontFamily="sans-serif">
        {table.name}
      </text>
      <text x={cx} y={cy + 9} textAnchor="middle" fill={colors.text} fontSize={9} opacity={0.85} fontFamily="sans-serif">
        {table.capacity} pax
      </text>
    </g>
  );
};

// Draw a rectangular/square table with chairs on all sides
const RectTable = ({ table, tx, ty, tw, th, colors, selected, isSelectable, onSelect, onHover }) => {
  const cx = tx + tw / 2;
  const cy = ty + th / 2;
  const chairW  = 16;
  const chairH  = 10;
  const gap     = 5;

  // Chairs along top/bottom
  const topChairs    = Math.max(1, Math.floor(tw / 28));
  const bottomChairs = topChairs;
  const sideChairs   = 1;

  const topY    = ty - chairH - gap;
  const bottomY = ty + th + gap;

  const colPositions = (count, tableX, tableW) => {
    const spacing = tableW / (count + 1);
    return Array.from({ length: count }, (_, i) => tableX + spacing * (i + 1));
  };

  return (
    <g
      style={{ cursor: isSelectable ? "pointer" : "not-allowed" }}
      onClick={() => isSelectable && onSelect(table)}
      onMouseEnter={() => onHover(table)}
      onMouseLeave={() => onHover(null)}
    >
      {/* Glow */}
      {selected && (
        <rect x={tx - 8} y={ty - 8} width={tw + 16} height={th + 16} rx={14} fill="none" stroke={colors.glow} strokeWidth={6} opacity={0.7} />
      )}
      {/* Top chairs */}
      {colPositions(topChairs, tx, tw).map((x, i) => (
        <rect key={`t${i}`} x={x - chairW / 2} y={topY} width={chairW} height={chairH} rx={4} fill={colors.chair} />
      ))}
      {/* Bottom chairs */}
      {colPositions(bottomChairs, tx, tw).map((x, i) => (
        <rect key={`b${i}`} x={x - chairW / 2} y={bottomY} width={chairW} height={chairH} rx={4} fill={colors.chair} />
      ))}
      {/* Side chairs */}
      <rect x={tx - chairH - gap} y={cy - chairW / 2} width={chairH} height={chairW} rx={4} fill={colors.chair} />
      <rect x={tx + tw + gap}     y={cy - chairW / 2} width={chairH} height={chairW} rx={4} fill={colors.chair} />
      {/* Table */}
      <rect x={tx} y={ty} width={tw} height={th} rx={8} fill={colors.table} />
      {selected && (
        <rect x={tx} y={ty} width={tw} height={th} rx={8} fill="none" stroke="#fff" strokeWidth={2.5} />
      )}
      {/* Label */}
      <text x={cx} y={cy - 5} textAnchor="middle" fill={colors.text} fontSize={10} fontWeight="bold" fontFamily="sans-serif">
        {table.name}
      </text>
      <text x={cx} y={cy + 8} textAnchor="middle" fill={colors.text} fontSize={9} opacity={0.85} fontFamily="sans-serif">
        {table.capacity} pax
      </text>
    </g>
  );
};

const TABLE_DIMS = {
  circle:    { w: 80,  h: 80  },
  rect:      { w: 100, h: 68  },
  rectangle: { w: 128, h: 68  },
};

const SVGFloorMap = ({ tables, bookedIds, selectedId, onSelect }) => {
  const [hovered, setHovered] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);

  const W = 860, H = 490;

  const handleHover = (table) => setHovered(table);

  return (
    <div className="relative w-full overflow-x-auto">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="w-full rounded-2xl"
        style={{ minWidth: 480, background: "transparent" }}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Floor */}
        <defs>
          <pattern id="wood" width="60" height="30" patternUnits="userSpaceOnUse" patternTransform="rotate(0)">
            <rect width="60" height="30" fill="#fef3c7" />
            <line x1="0" y1="0" x2="60" y2="0" stroke="#fde68a" strokeWidth="0.8" opacity="0.6" />
            <line x1="0" y1="15" x2="60" y2="15" stroke="#fde68a" strokeWidth="0.4" opacity="0.4" />
          </pattern>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#00000020" />
          </filter>
          <radialGradient id="floorGrad" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#fffbeb" />
            <stop offset="100%" stopColor="#fef3c7" />
          </radialGradient>
        </defs>

        {/* Floor background */}
        <rect x={0} y={0} width={W} height={H} fill="url(#floorGrad)" />
        <rect x={0} y={0} width={W} height={H} fill="url(#wood)" opacity={0.5} />

        {/* Outer walls */}
        <rect x={10} y={10} width={W - 20} height={H - 20} rx={16} fill="none" stroke="#d97706" strokeWidth={3} opacity={0.35} />
        <rect x={18} y={18} width={W - 36} height={H - 36} rx={12} fill="none" stroke="#fbbf24" strokeWidth={1} opacity={0.2} />

        {/* Floor label */}
        <text x={30} y={40} fill="#d97706" fontSize={11} fontWeight="600" opacity={0.5} fontFamily="sans-serif" letterSpacing="2">
          RESTAURANT FLOOR
        </text>

        {/* Decorative: bar counter top-right */}
        <rect x={W - 120} y={20} width={100} height={32} rx={8} fill="#92400e" opacity={0.15} />
        <text x={W - 70} y={40} textAnchor="middle" fill="#92400e" fontSize={9} fontWeight="600" opacity={0.5} fontFamily="sans-serif">
          BAR
        </text>

        {/* Decorative: entrance bottom-left */}
        <rect x={20} y={H - 52} width={80} height={32} rx={8} fill="#92400e" opacity={0.1} />
        <text x={60} y={H - 32} textAnchor="middle" fill="#92400e" fontSize={9} fontWeight="600" opacity={0.5} fontFamily="sans-serif">
          ENTRANCE
        </text>

        {/* Decorative: plants/corners */}
        {[[36, 70], [W - 36, 70], [36, H - 70], [W - 36, H - 70]].map(([px, py], i) => (
          <g key={i}>
            <circle cx={px} cy={py} r={12} fill="#86efac" opacity={0.25} />
            <circle cx={px} cy={py} r={7}  fill="#22c55e" opacity={0.2} />
          </g>
        ))}

        {/* Tables */}
        {tables.map((table) => {
          const dims = TABLE_DIMS[table.shape] || TABLE_DIMS.circle;
          const isBooked = bookedIds.has(table.name);
          const effectiveStatus = isBooked ? "reserved" : (table.status || "available");
          const colors      = STATUS_COLORS[effectiveStatus] || STATUS_COLORS.available;
          const isSelectable = effectiveStatus === "available";
          const selected     = selectedId === table.id;

          const px = Number(table.position_x ?? 140);
          const py = Number(table.position_y ?? 140);

          if (table.shape === "circle") {
            const r = 26;
            return (
              <CircleTable
                key={table.id}
                table={table}
                cx={px + dims.w / 2}
                cy={py + dims.h / 2}
                r={r}
                colors={colors}
                selected={selected}
                isSelectable={isSelectable}
                onSelect={onSelect}
                onHover={handleHover}
              />
            );
          }
          return (
            <RectTable
              key={table.id}
              table={table}
              tx={px + 14}
              ty={py + 14}
              tw={dims.w - 28}
              th={dims.h - 28}
              colors={colors}
              selected={selected}
              isSelectable={isSelectable}
              onSelect={onSelect}
              onHover={handleHover}
            />
          );
        })}

        {/* Empty state */}
        {tables.length === 0 && (
          <>
            <text x={W / 2} y={H / 2 - 16} textAnchor="middle" fill="#d97706" fontSize={16} opacity={0.3} fontFamily="sans-serif">
              🍽
            </text>
            <text x={W / 2} y={H / 2 + 10} textAnchor="middle" fill="#d97706" fontSize={13} opacity={0.35} fontFamily="sans-serif">
              Tables coming soon
            </text>
          </>
        )}
      </svg>

      {/* Hover tooltip */}
      {hovered && (
        <div
          className="absolute z-10 bg-gray-900 text-white text-xs rounded-xl px-3 py-2 pointer-events-none shadow-xl"
          style={{ top: 12, right: 12 }}
        >
          <p className="font-bold text-sm">{hovered.name}</p>
          <p className="text-gray-300 mt-0.5 flex items-center gap-1">
            <Users size={10} /> {hovered.capacity} seats · {hovered.shape}
          </p>
          <p className={`mt-1 font-semibold ${
            bookedIds.has(String(hovered.id)) ? "text-amber-400" :
            hovered.status === "available" ? "text-emerald-400" : "text-red-400"
          }`}>
            {bookedIds.has(String(hovered.id)) ? "Reserved" : hovered.status === "available" ? "✓ Available" : "Unavailable"}
          </p>
        </div>
      )}
    </div>
  );
};

// ── Date Strip ────────────────────────────────────────────────────────────────
const DateStrip = ({ value, onChange }) => {
  const [weekStart, setWeekStart] = useState(0);
  const week = DAYS.slice(weekStart, weekStart + 7);
  const canPrev = weekStart > 0;
  const canNext = weekStart + 7 < DAYS.length;

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => setWeekStart((w) => Math.max(0, w - 7))}
        disabled={!canPrev}
        className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl border border-gray-200 text-gray-500 hover:border-orange-300 hover:text-orange-500 disabled:opacity-30 disabled:cursor-not-allowed transition"
      >
        ‹
      </button>

      <div className="flex gap-2 flex-1">
        {week.map((d) => {
          const active = d.iso === value;
          return (
            <button
              key={d.iso}
              onClick={() => onChange(d.iso)}
              className={`flex-1 flex flex-col items-center py-3 rounded-2xl border-2 transition-all font-semibold ${
                active
                  ? "bg-orange-500 border-orange-500 text-white shadow-lg shadow-orange-200"
                  : "bg-white border-gray-100 text-gray-600 hover:border-orange-200 hover:bg-orange-50"
              }`}
            >
              <span className={`text-[11px] uppercase tracking-wide ${active ? "text-orange-100" : "text-gray-400"}`}>
                {d.day}
              </span>
              <span className="text-lg font-extrabold leading-tight">{d.date}</span>
              <span className={`text-[10px] ${active ? "text-orange-100" : "text-gray-400"}`}>{d.month}</span>
            </button>
          );
        })}
      </div>

      <button
        onClick={() => setWeekStart((w) => Math.min(DAYS.length - 7, w + 7))}
        disabled={!canNext}
        className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-xl border border-gray-200 text-gray-500 hover:border-orange-300 hover:text-orange-500 disabled:opacity-30 disabled:cursor-not-allowed transition"
      >
        ›
      </button>
    </div>
  );
};

// ── Time Slots ────────────────────────────────────────────────────────────────
const TimeSlots = ({ value, onChange }) => (
  <div className="space-y-2">
    {[{ label: "☀ Lunch", slots: LUNCH_SLOTS }, { label: "🌙 Dinner", slots: DINNER_SLOTS }].map(({ label, slots }) => (
      <div key={label}>
        <p className="text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wide">{label}</p>
        <div className="flex flex-wrap gap-2">
          {slots.map((t) => (
            <button
              key={t}
              onClick={() => onChange(t)}
              className={`px-3 py-1.5 rounded-xl text-sm font-semibold border transition-all ${
                value === t
                  ? "bg-orange-500 border-orange-500 text-white shadow-md"
                  : "bg-white border-gray-200 text-gray-700 hover:border-orange-300 hover:bg-orange-50"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// ── Booking Form Steps ────────────────────────────────────────────────────────
const inp = "w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 transition placeholder-gray-400";

const BookingForm = ({ table, date, time, onClose, onBooked }) => {
  const { customerUser } = useAppContext();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ customer_name: customerUser?.name || "", customer_phone: customerUser?.phone || "", customer_email: customerUser?.email || "", party_size: Math.min(2, table.capacity), notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.customer_name.trim()) { setError("Name is required"); return; }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/reservations`, {
        license_id:      LICENSE_ID,
        user_id:         customerUser?.id || null,
        table_name:      table.name,
        customer_name:   form.customer_name,
        customer_phone:  form.customer_phone,
        customer_email:  form.customer_email,
        party_size:      Number(form.party_size),
        date, time,
        notes:  form.notes,
        status: "pending",
      });
      setDone(true);
      onBooked?.();
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) return (
    <div className="flex flex-col items-center text-center py-6 space-y-4">
      <div className="relative">
        <div className="w-20 h-20 bg-emerald-500 rounded-full flex items-center justify-center shadow-xl shadow-emerald-200">
          <Check size={38} className="text-white" />
        </div>
        <div className="absolute -top-1 -right-1 w-7 h-7 bg-orange-400 rounded-full flex items-center justify-center">
          <Sparkles size={14} className="text-white" />
        </div>
      </div>
      <div>
        <h3 className="text-xl font-extrabold text-gray-900">You're booked!</h3>
        <p className="text-gray-500 text-sm mt-1">
          <strong>{table.name}</strong> for <strong>{form.customer_name}</strong>
        </p>
        <div className="flex items-center justify-center gap-3 mt-2 text-sm text-gray-600">
          <span className="flex items-center gap-1"><Calendar size={13} className="text-orange-500" />{date}</span>
          <span className="flex items-center gap-1"><Clock size={13} className="text-orange-500" />{time}</span>
          <span className="flex items-center gap-1"><Users size={13} className="text-orange-500" />{form.party_size}</span>
        </div>
      </div>
      <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-xs text-amber-800 max-w-xs">
        We'll confirm your booking soon. If you don't hear from us within the hour, please call us.
      </div>
      <button
        onClick={onClose}
        className="px-8 py-2.5 bg-orange-500 text-white rounded-xl font-bold text-sm hover:bg-orange-600 transition shadow-lg shadow-orange-100"
      >
        Back to Floor Plan
      </button>
    </div>
  );

  const steps = [
    { label: "Party",   icon: Users     },
    { label: "Details", icon: Mail      },
    { label: "Confirm", icon: Check     },
  ];

  return (
    <div className="space-y-5">
      {/* Step bar */}
      <div className="flex items-center">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all text-sm font-bold ${
              i < step  ? "bg-emerald-500 border-emerald-500 text-white"
              : i === step ? "bg-orange-500 border-orange-500 text-white shadow-md shadow-orange-200"
              : "bg-white border-gray-200 text-gray-400"
            }`}>
              {i < step ? <Check size={14} /> : i + 1}
            </div>
            <div className={`flex-1 h-0.5 mx-1 ${i < step ? "bg-emerald-400" : "bg-gray-100"}`} />
          </div>
        ))}
      </div>

      {/* Booking summary strip */}
      <div className="flex items-center gap-2 bg-orange-50 rounded-xl px-3 py-2.5 border border-orange-100">
        <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
          <MapPin size={14} className="text-white" />
        </div>
        <div className="flex-1 min-w-0 text-xs text-gray-700">
          <span className="font-bold">{table.name}</span>
          <span className="text-gray-400"> · </span>
          <span>{date}</span>
          <span className="text-gray-400"> · </span>
          <span>{time}</span>
        </div>
      </div>

      {/* Step 0: Party size */}
      {step === 0 && (
        <div className="space-y-4">
          <div>
            <p className="text-sm font-bold text-gray-800 mb-3">How many guests?</p>
            <div className="flex items-center justify-center gap-5 py-4">
              <button
                onClick={() => set("party_size", Math.max(1, form.party_size - 1))}
                className="w-12 h-12 rounded-full border-2 border-gray-200 text-2xl font-bold text-gray-600 flex items-center justify-center hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50 transition active:scale-95"
              >
                −
              </button>
              <div className="text-center">
                <span className="text-5xl font-extrabold text-gray-900">{form.party_size}</span>
                <p className="text-xs text-gray-400 mt-0.5">guests</p>
              </div>
              <button
                onClick={() => set("party_size", Math.min(table.capacity, form.party_size + 1))}
                className="w-12 h-12 rounded-full border-2 border-gray-200 text-2xl font-bold text-gray-600 flex items-center justify-center hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50 transition active:scale-95"
              >
                +
              </button>
            </div>
            <div className="flex justify-center gap-2 flex-wrap mt-2">
              {Array.from({ length: table.capacity }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  onClick={() => set("party_size", n)}
                  className={`w-9 h-9 rounded-full text-sm font-bold border-2 transition ${
                    form.party_size === n
                      ? "bg-orange-500 border-orange-500 text-white"
                      : "border-gray-200 text-gray-600 hover:border-orange-300"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <p className="text-center text-xs text-gray-400 mt-2">
              This table seats up to {table.capacity} guests
            </p>
          </div>
          <button
            onClick={() => setStep(1)}
            className="w-full py-3 bg-orange-500 text-white rounded-xl font-bold text-sm hover:bg-orange-600 transition flex items-center justify-center gap-2 shadow-md shadow-orange-100"
          >
            Continue <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Step 1: Guest details */}
      {step === 1 && (
        <div className="space-y-3">
          <p className="text-sm font-bold text-gray-800 mb-1">Your contact details</p>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Full Name <span className="text-red-400">*</span></label>
            <input className={inp} value={form.customer_name} onChange={e => set("customer_name", e.target.value)} placeholder="e.g. John Smith" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Phone</label>
              <input className={inp} type="tel" value={form.customer_phone} onChange={e => set("customer_phone", e.target.value)} placeholder="+1 555 0100" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Email</label>
              <input className={inp} type="email" value={form.customer_email} onChange={e => set("customer_email", e.target.value)} placeholder="you@mail.com" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">Special Requests</label>
            <textarea
              className={inp + " resize-none"}
              rows={2}
              value={form.notes}
              onChange={e => set("notes", e.target.value)}
              placeholder="Allergies, birthday, high chair, window seat…"
            />
          </div>
          {error && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{error}</p>}
          <div className="flex gap-2 pt-1">
            <button onClick={() => setStep(0)} className="flex items-center gap-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition">
              <ArrowLeft size={14} /> Back
            </button>
            <button
              onClick={() => {
                if (!form.customer_name.trim()) { setError("Name is required"); return; }
                setError(""); setStep(2);
              }}
              className="flex-1 py-2.5 bg-orange-500 text-white rounded-xl font-bold text-sm hover:bg-orange-600 transition flex items-center justify-center gap-2 shadow-md shadow-orange-100"
            >
              Review Booking <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Confirm */}
      {step === 2 && (
        <div className="space-y-4">
          <p className="text-sm font-bold text-gray-800 mb-1">Confirm your reservation</p>
          <div className="bg-gray-50 rounded-2xl p-4 space-y-2.5">
            {[
              ["Table",  table.name],
              ["Date",   date],
              ["Time",   time],
              ["Guests", `${form.party_size} person${form.party_size > 1 ? "s" : ""}`],
              ["Name",   form.customer_name],
              form.customer_phone && ["Phone", form.customer_phone],
              form.customer_email && ["Email", form.customer_email],
              form.notes && ["Notes", form.notes],
            ].filter(Boolean).map(([label, value]) => (
              <div key={label} className="flex items-start justify-between gap-2 text-sm">
                <span className="text-gray-400 font-medium flex-shrink-0">{label}</span>
                <span className="font-semibold text-gray-800 text-right">{value}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 bg-blue-50 rounded-xl p-3 text-xs text-blue-700">
            <Star size={13} className="flex-shrink-0 fill-blue-400 text-blue-400" />
            Reservation is pending until confirmed by our team.
          </div>
          {error && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle size={12} />{error}</p>}
          <div className="flex gap-2">
            <button onClick={() => setStep(1)} className="flex items-center gap-1 px-4 py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 transition">
              <ArrowLeft size={14} /> Back
            </button>
            <button
              onClick={submit}
              disabled={submitting}
              className="flex-1 py-3 bg-emerald-500 text-white rounded-xl font-bold text-sm hover:bg-emerald-600 disabled:opacity-60 transition flex items-center justify-center gap-2 shadow-lg shadow-emerald-100"
            >
              {submitting ? <RefreshCw size={14} className="animate-spin" /> : <Check size={15} />}
              Confirm Booking
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Legend ─────────────────────────────────────────────────────────────────
const Legend = () => (
  <div className="flex flex-wrap items-center gap-4">
    {[
      { color: "bg-emerald-500", label: "Available — click to book" },
      { color: "bg-amber-400",   label: "Reserved"                  },
      { color: "bg-red-500",     label: "Occupied"                  },
      { color: "bg-slate-300",   label: "Unavailable"               },
    ].map(({ color, label }) => (
      <div key={label} className="flex items-center gap-2 text-xs text-gray-500">
        <span className={`w-3 h-3 rounded-full flex-shrink-0 ${color}`} />
        {label}
      </div>
    ))}
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────
const Reservations = () => {
  const [tables,       setTables]       = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [date,         setDate]         = useState(TODAY);
  const [time,         setTime]         = useState("19:00");
  const [selectedTable, setSelectedTable] = useState(null);
  const [searchParams]  = useSearchParams();
  const preselect       = searchParams.get("table");
  const floorRef        = useRef(null);

  const loadData = async (d = date) => {
    setLoading(true);
    try {
      const [tRes, aRes] = await Promise.all([
        axios.get(`${API}/licenseTable`),
        axios.get(`${API}/reservations?date=${d}&license_id=${LICENSE_ID}`),
      ]);
      setTables(tRes.data?.list || []);
      setAppointments(aRes.data?.list || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(date); }, [date]);

  const bookedIds = useMemo(() => {
    const s = new Set();
    appointments.forEach(a => {
      if (a.table_name && a.status !== "cancelled") s.add(a.table_name);
    });
    return s;
  }, [appointments]);

  const available = tables.filter(t => t.status === "available" && !bookedIds.has(t.name)).length;

  // Auto-select table when arriving via QR code / link (?table=NAME)
  useEffect(() => {
    if (!preselect || loading || tables.length === 0 || selectedTable) return;
    const match = tables.find(t => t.name.toLowerCase() === preselect.toLowerCase());
    if (match && match.status === "available" && !bookedIds.has(match.name)) {
      setSelectedTable(match);
      setTimeout(() => floorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [preselect, loading, tables, bookedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (table) => setSelectedTable(table);
  const handleClose  = () => { setSelectedTable(null); loadData(date); };
  const handleBooked = () => loadData(date);

  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      {/* ── Hero ─────────────────────────────────────────── */}
      <div className="relative bg-gradient-to-br from-orange-700 via-orange-600 to-orange-500 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 80%, #fff 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        <div className="relative max-w-5xl mx-auto px-4 py-14 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            <UtensilsCrossed size={15} /> Book Your Table
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight leading-tight">
            Reserve Your Seat
          </h1>
          <p className="mt-3 text-orange-100 text-base max-w-lg mx-auto">
            Pick your date, choose your table on our interactive floor plan, and you're set.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-6 space-y-5">

        {/* ── Date picker ──────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Select a Date</p>
          <DateStrip value={date} onChange={(d) => { setDate(d); setSelectedTable(null); }} />
        </div>

        {/* ── Time picker ──────────────────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Select a Time</p>
          <TimeSlots value={time} onChange={setTime} />
        </div>

        {/* ── Floor map + Booking panel ─────────────────────── */}
        <div ref={floorRef} className={`flex gap-4 items-start transition-all duration-300 ${selectedTable ? "flex-col lg:flex-row" : ""}`}>

          {/* Map */}
          <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 p-4 transition-all duration-300 ${selectedTable ? "lg:flex-1" : "w-full"}`}>
            {/* Map header */}
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Floor Plan</p>
                <p className="text-sm text-gray-600 mt-0.5">
                  <span className="font-bold text-emerald-600">{available}</span> table{available !== 1 ? "s" : ""} available · {date} at {time}
                </p>
              </div>
              {loading && <RefreshCw size={16} className="text-gray-300 animate-spin" />}
            </div>

            <Legend />

            <div className="mt-4">
              {loading ? (
                <div className="flex items-center justify-center py-24 text-gray-300">
                  <RefreshCw size={28} className="animate-spin mr-3" />
                  <span className="text-sm">Loading floor plan…</span>
                </div>
              ) : (
                <SVGFloorMap
                  tables={tables}
                  bookedIds={bookedIds}
                  selectedId={selectedTable?.id}
                  onSelect={handleSelect}
                />
              )}
            </div>
          </div>

          {/* Booking panel — inline on desktop, slides below on mobile */}
          {selectedTable && (
            <div className="lg:w-96 w-full bg-white rounded-2xl shadow-lg border border-orange-100 overflow-hidden flex-shrink-0">
              {/* Panel header */}
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-5 py-4 flex items-center justify-between">
                <div>
                  <p className="text-white font-extrabold text-base">{selectedTable.name}</p>
                  <p className="text-orange-100 text-xs mt-0.5">
                    Up to {selectedTable.capacity} guests · {selectedTable.shape}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedTable(null)}
                  className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition"
                >
                  <X size={15} className="text-white" />
                </button>
              </div>
              <div className="p-5">
                <BookingForm
                  key={selectedTable.id}
                  table={selectedTable}
                  date={date}
                  time={time}
                  onClose={handleClose}
                  onBooked={handleBooked}
                />
              </div>
            </div>
          )}
        </div>

        {/* ── Prompt when nothing selected ─────────────────── */}
        {!selectedTable && !loading && tables.length > 0 && (
          <div className="text-center py-4 text-gray-400 text-sm flex items-center justify-center gap-2">
            <span className="text-lg">☝</span>
            Click any <span className="font-bold text-emerald-600">green table</span> on the floor plan to start your reservation
          </div>
        )}

        {/* ── Info cards ───────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
          {[
            { emoji: "⚡", title: "Book in 60 seconds",     body: "Pick a table, fill your name, done. No account needed." },
            { emoji: "✅", title: "Team confirmation",       body: "We'll confirm your booking via phone or email shortly."  },
            { emoji: "📞", title: "Need help?",              body: "Call us directly and we'll arrange everything for you." },
          ].map(({ emoji, title, body }) => (
            <div key={title} className="bg-white rounded-2xl border border-gray-100 p-4 flex gap-3">
              <span className="text-2xl flex-shrink-0">{emoji}</span>
              <div>
                <p className="text-sm font-bold text-gray-900">{title}</p>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Reservations;
